// HU-14 · Redactar el informe FAR-03 (F-04, Sprint 2; rev. 15, 10 criterios).
//
// UNA PESTAÑA DE LA EVALUACIÓN. El informe se COMPONE desde la evaluación (RN-32): cinco de
// las ocho secciones —fecha de reunión, participantes, análisis de causas, solución
// inmediata y preventiva— vienen de HU-12/HU-13/HU-43 y «se corrigen en la evaluación del
// caso» (C8). Las otras tres —Antecedentes, Demanda y Conclusión— se redactan aquí (C7).
//
//   · «Elaborar FAR-03» crea el borrador con los datos cargados (C1) y SOLO con la
//     evaluación completa —los 28 puntos, causalidad y conclusión, gravedad— (orden del
//     operador, 18-sep-2026): con algo pendiente el botón queda deshabilitado y la lista
//     de faltantes lleva a la pestaña que toca; si aun así llega un 422, se muestra (C4).
//     Elaborar NO mueve el caso: sigue en la bandeja del Comité con su borrador.
//   · Las ocho secciones, con los nombres del procedimiento y en su orden (C2); las
//     soluciones separadas por tipo (C3); «Algoritmo aplicado: …» en el análisis (C5); el
//     plazo interno medido al elaborar (C6).
//   · «Enviar a aprobación» solo con Antecedentes, Demanda y Conclusión redactados (C9);
//     congela la versión (C9/C10) y ES ENTONCES cuando el caso pasa a «en aprobación»:
//     desde entonces nada se edita y la pantalla dice «El informe está en aprobación». Un
//     observado se corrige y se reenvía como la versión siguiente; las anteriores se
//     consultan tal cual (C10).
//   · La aprobación (aprobar u observar) ya NO vive aquí: el aprobador lee la versión
//     congelada en `AprobacionDelInforme.tsx` (HU-17, `ui/MG-ILE-43.md`). Esta es la vista
//     del Comité, de Asuntos Regulatorios y del invitado.
//
// SPRINT 3 (`ui/MG-ILE-44.md`, `ui/MG-ILE-898.md`, tareas 18-4 y 898-3). Las acciones salen de
// `acciones_disponibles` del servidor (§8.1 R4): «Enviar a aprobación» con `enviar`, la
// redacción con `redactar` y el panel de subsanación con `subsanar` (solo el Comité, D-S3-03;
// Asuntos Regulatorios ve el motivo y las rondas en solo lectura, HU-18-C8). `soloLectura` es
// la máscara de la sesión simulada: resta, nunca agrega. El autor ya no viaja en el cuerpo
// (AT S3 §2.0): lo pone el servidor desde la sesión.

import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, ChevronDown, ChevronRight, FileText, History, Lock, RefreshCw, Save, Send } from 'lucide-react';
import { Alert, Button, Card, DataTable, Field, Mono, StatusBadge, Textarea } from './ui';
import type { ColumnaDeTabla } from './ui';
import {
  ESTADOS_DE_ACCION, MENSAJE_SIN_CAUSALIDAD, TIPOS_DE_ACCION, TOPE_DE_SECCION_DEL_ESQUEMA,
  elaborarInforme, enviarInformeAAprobacion,
  mensajeDeTopeSuperado, ofrece, redactarInforme, subsanarInforme, verInforme,
} from '../api/evaluacion';
import type { FaltanteDelInforme, Informe, SeccionDelInforme } from '../api/evaluacion';
import { aFallo } from '../api/cliente';
import { listarCatalogo } from '../api/catalogos';
import type { RespuestaDeCatalogo } from '../api/catalogos';
import type { Fallo } from '../api/cliente';
import { estadoDelInforme } from '../lib/etiquetas';
import RespuestasDelInstructivo from './RespuestasDelInstructivo';
import type { RespuestaResumida } from './RespuestasDelInstructivo';
import { comoFechaDeInstante } from '../lib/fechas';

const REDACTABLES = new Set(['antecedentes', 'demanda', 'conclusion']);

/** HU-18-C2: el texto bajo «Qué se subsanó» vacío (el 422 del servidor trae el mismo). */
export const MENSAJE_SIN_SUBSANACION = 'Di qué se subsanó: el aprobador necesita saber qué cambió.';
/** El esquema HTTP (`SubsanacionEntrada.que_se_subsano`, `max_length=2000`). */
const TOPE_DE_LA_SUBSANACION = 2000;

// DD/MM/AAAA (lineamiento, Textos): `lib/fechas.ts`, nunca el ISO recortado. RN-82: el día se
// toma en la zona del país del caso, que llega por `zonaHoraria`.
const fecha = (iso: string | null | undefined, zona: string | null): string => comoFechaDeInstante(iso, zona) || '—';

/**
 * HU-14-C6 / HU-13-C8: los nombres de `clase_accion` y `responsable_accion` para la sección
 * de acciones. Se leen una vez; si fallan, la sección muestra el código (nunca queda vacía).
 * La usan la vista del Comité y la del aprobador.
 */
export function useNombresDeLasAcciones(): NombreDe {
  const [nombres, setNombres] = useState<Record<string, string>>({});
  useEffect(() => {
    let vivo = true;
    Promise.all(['clase_accion', 'responsable_accion'].map(catalogoOVacio))
      .then((listas) => { if (vivo) setNombres(nombresDelCatalogo(listas)); });
    return () => { vivo = false; };
  }, []);
  return (codigo) => {
    const clave = String(codigo ?? '');
    return nombres[clave] ?? clave;
  };
}

const COLUMNAS_DE_RONDAS: ColumnaDeTabla[] = [{ key: 'texto', label: 'Ronda' }];

/**
 * HU-18-C3: el historial de rondas, plegable. Cada fila es el `texto` que arma el servidor
 * («Ronda 1 · J. Salas · 06/10/2026 · Falta el lote · Subsanado por M. Díaz: …»); la vigente
 * termina en «pendiente». El mismo campo que lee el aprobador (`aprobacion-rondas`).
 */
function RondasAnteriores({ informe }: Readonly<{ informe: Informe }>) {
  const [abierto, setAbierto] = useState(false);
  const rondas = informe.rondas ?? [];
  if (rondas.length === 0) return null;
  return (
    <div>
      <Button data-testid="subsanacion-rondas-desplegar" size="sm" variant="ghost" icon={abierto ? ChevronDown : ChevronRight}
        aria-expanded={abierto} onClick={() => setAbierto((x) => !x)}>
        Rondas anteriores ({rondas.length})
      </Button>
      {abierto && (
        <div className="mt-2">
          <DataTable data-testid="subsanacion-rondas" testidDeFila="subsanacion-rondas-fila" columns={COLUMNAS_DE_RONDAS}
            rows={rondas.map((r) => ({ id: r.ronda, texto: r.texto }))} />
        </div>
      )}
    </div>
  );
}

/** La observación vigente: la última sin subsanar (`observaciones[]` del servidor, HU-18-C1/C8). */
const observacionVigente = (informe: Informe) =>
  [...informe.observaciones].reverse().find((o) => !o.subsanado_en) ?? null;

/** HU-40-C7: el bloque de aprobación de la versión vigente, sin imagen de firma: el texto de su constancia. */
const constanciaDeLaAprobacion = (informe: Informe) =>
  (informe.constancias ?? []).find((c) => c.accion === 'aprobacion' && c.version === informe.version) ?? null;

// El "1250 / 20000 caracteres" de la sección (sin separador de miles, como el mensaje del
// API y el resto de contadores); el tope siempre existe (ver `topeDeSeccion`).
const cuentaAtras = (valor: string, tope: number): string => `${valor.length} / ${tope} caracteres`;

/** Código de catálogo → nombre legible; si el catálogo no cargó, se ve el código. */
type NombreDe = (codigo: unknown) => string;

/** Un catálogo que no carga no tumba el informe: llega vacío y la sección muestra el código. */
const catalogoOVacio = (catalogo: string): Promise<RespuestaDeCatalogo> =>
  listarCatalogo(catalogo).catch((): RespuestaDeCatalogo => ({ catalogo, valores: [] }));

/** Código → nombre de todos los catálogos juntos; el que no trae nombre se queda con su código. */
const nombresDelCatalogo = (listas: RespuestaDeCatalogo[]): Record<string, string> =>
  Object.fromEntries(listas.flatMap((l) => l.valores.map((v) => [v.codigo, v.nombre ?? v.codigo] as const)));

function Seccion({ seccion, informe, actor, onCambio, onFallo, onGuardando, nombreDe, tope }: Readonly<{
  seccion: SeccionDelInforme; informe: Informe; actor: string; onCambio: (i: Informe) => void; onFallo: (f: Fallo | null) => void;
  /** HU-14 (QA, 18-sep-2026): el tope de caracteres de la sección; el campo no deja pasarlo. */
  tope: number;
  /** HU-14-C6 / HU-13-C8: las filas de acciones traen CÓDIGOS (`retention_lote`,
   *  `gestion_calidad`); el informe que ve el aprobador imprime los nombres del catálogo,
   *  como la pestaña Acciones. El contenido congelado sigue guardando el código. */
  nombreDe: NombreDe;
  /** Avisa al padre mientras el guardado esta en vuelo: «Enviar a aprobacion» se bloquea
   *  hasta que la redaccion quedo persistida, o la version congelaria el texto anterior
   *  (contra una base remota el PATCH tarda segundos y el envio le ganaba la carrera). */
  onGuardando?: (activo: boolean) => void;
}>) {
  const [texto, setTexto] = useState(seccion.texto ?? '');
  const [ocupado, setOcupado] = useState(false);
  // El aviso bajo el campo: se va en cuanto se escribe (mismo trato que la evaluación).
  const [error, setError] = useState<string | null>(null);
  // El acuse de guardado, junto al botón. Ocho secciones en una pantalla larga: sin él,
  // guardar la tercera no se distinguía de no haber pulsado nada (22-sep-2026).
  const [guardado, setGuardado] = useState(false);
  useEffect(() => { setTexto(seccion.texto ?? ''); }, [seccion.texto]);

  const guardar = async () => {
    // QA (18-sep-2026): el 422 «String should have at most 20000 characters» del API es
    // una validación, no una caída; se atiende ANTES de enviar, bajo el campo. `maxLength`
    // no cubre un texto ya guardado más largo que el tope vigente.
    if (texto.length > tope) {
      setError(mensajeDeTopeSuperado(`El texto de «${seccion.titulo}»`, texto.length, tope));
      return;
    }
    onFallo(null);
    setError(null);
    setOcupado(true);
    onGuardando?.(true);
    try {
      onCambio(await redactarInforme(informe.caso_id, seccion.clave as 'antecedentes' | 'demanda' | 'conclusion', texto, actor));
      setGuardado(true);
    } catch (e) {
      const fallo = aFallo(e);
      // Si el servidor lo rechazó por largo (el tope pudo bajar en la base), el aviso va
      // bajo el campo, que es donde hay que corregirlo; el resto sigue en la alerta.
      if (fallo.estado === 422 && /caracteres|too_long|demasiado largo/i.test(fallo.message)) setError(fallo.message);
      else onFallo(fallo);
    } finally {
      setOcupado(false);
      onGuardando?.(false);
    }
  };

  // B8 (QA 01-10-2026): las tres secciones que se redactan aquí son obligatorias para enviar
  // (C9) y no lo decía nada hasta leer la pista del botón. Asterisco tras el título, como
  // `Field`, y `aria-required` en el campo para el lector de pantalla.
  const obligatoria = seccion.editable && REDACTABLES.has(seccion.clave);
  return (
    <section aria-label={`${seccion.numero}. ${seccion.titulo}`} className="border border-hairline rounded-xl p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Mono className="text-meta">{seccion.numero}</Mono>
        <h3 className="text-body font-semibold text-ink">{seccion.titulo}</h3>
        {obligatoria && <span data-testid="informe-seccion-obligatoria" className="-ml-1.5 font-semibold text-rose-700" aria-hidden="true">*</span>}
        {!seccion.editable && (
          <StatusBadge tone="neutral" icon={Lock}>
            {REDACTABLES.has(seccion.clave) ? (informe.motivo_de_no_editable ?? 'Solo lectura') : (seccion.nota ?? 'Solo lectura')}
          </StatusBadge>
        )}
      </div>
      {seccion.editable ? (
        <>
          {/* B2 (QA 01-10-2026): sin etiqueta visible. «Texto de «Antecedentes»» repetía el título
              de la sección justo encima; el nombre accesible del campo sigue siendo el mismo. */}
          <Field error={error} contador={cuentaAtras(texto, tope)}>
            <Textarea aria-label={`Texto de ${seccion.titulo}`} aria-required={obligatoria || undefined} rows={4} value={texto} maxLength={tope}
              onChange={(e) => { setTexto(e.target.value); setError(null); setGuardado(false); }} />
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" icon={Save} onClick={() => { guardar(); }} disabled={ocupado || texto === (seccion.texto ?? '')}>
              {ocupado ? 'Guardando…' : `Guardar ${seccion.titulo.toLowerCase()}`}
            </Button>
            {guardado && !ocupado && (
              <span className="inline-flex items-center gap-1 text-meta text-emerald-700" role="status">
                <CheckCircle2 size={13} aria-hidden="true" /> Guardado
              </span>
            )}
          </div>
        </>
      ) : (
        <ContenidoDeSoloLectura seccion={seccion} nombreDe={nombreDe} />
      )}
    </section>
  );
}

/** El tono del estado del compromiso (C-08). Tres estados, tres lecturas: cumplido, en
 *  marcha y sin empezar. La insignia era BINARIA y «en curso» se leía «Pendiente» — es
 *  decir, el informe decía que nadie había empezado un trabajo que sí estaba en marcha. */
const TONO_DEL_ESTADO: Record<string, 'success' | 'info' | 'neutral'> = {
  cumplida: 'success', en_curso: 'info', pendiente: 'neutral',
};

/** La etiqueta de un código de catálogo cerrado, o el código si llegara uno desconocido:
 *  preferimos enseñar el dato crudo a dejar un hueco donde había información. */
const etiquetaDe = (catalogo: ReadonlyArray<{ value: string; label: string }>, codigo: unknown) =>
  catalogo.find((o) => o.value === codigo)?.label ?? comoTexto(codigo);

/** Una fila de acciones (HU-13) tal como la imprime el informe: tipo, qué, quién, plazo y estado.
 *
 *  El TIPO delante no es decoración (bug 638): la sección 6 lleva solo las inmediatas, pero
 *  la 7 es «todo lo demás» —preventivas y correctivas hoy, y lo que Administración agregue
 *  mañana—, así que sin él esa lista no permite distinguir una acción preventiva de una
 *  correctiva, que es justo lo que HU-13-C8 pide poder demostrar. */
function FilaDeAccion({ fila, nombreDe }: Readonly<{ fila: Record<string, unknown>; nombreDe: NombreDe }>) {
  const estado = comoTexto(fila.estado);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <StatusBadge tone="neutral" dot={false}>{etiquetaDe(TIPOS_DE_ACCION, fila.tipo)}</StatusBadge>
      <span>{nombreDe(fila.que_se_hace)} · {nombreDe(fila.responsable)} · plazo {comoTexto(fila.plazo)}</span>
      <StatusBadge tone={TONO_DEL_ESTADO[estado] ?? 'neutral'}>{etiquetaDe(ESTADOS_DE_ACCION, fila.estado)}</StatusBadge>
    </span>
  );
}

/** Lo que se lee de una sección no redactable: la ampliación por bloques, una lista de filas o el texto compuesto. */
export function ContenidoDeSoloLectura({ seccion, nombreDe }: Readonly<{ seccion: SeccionDelInforme; nombreDe: NombreDe }>) {
  if (seccion.clave === 'ampliacion') return <SeccionDeLaAmpliacion seccion={seccion} />;
  if (seccion.filas.length === 0) {
    return <p className="text-body whitespace-pre-wrap">{seccion.texto ?? <span className="text-ink-soft">Sin contenido todavía.</span>}</p>;
  }
  return (
    <ol className="text-body list-decimal pl-5 space-y-1" aria-label={`Lista de ${seccion.titulo}`}>
      {/* La clave sale del CONTENIDO de la fila (id, o qué se hace, o el texto) y el índice
          solo desempata: con el índice a secas, quitar la primera acción re-pintaba todas. */}
      {seccion.filas.map((f, i) => (
        <li key={`${comoTexto(f.id ?? f.que_se_hace ?? f.texto ?? f.nombre)}-${i}`}>
          {'que_se_hace' in f ? <FilaDeAccion fila={f} nombreDe={nombreDe} /> : comoTexto(f.texto ?? f.nombre)}
        </li>
      ))}
    </ol>
  );
}

/**
 * HU-49-C4 · Bug 718: la sección «Información adicional del EA grave» se lee por bloques.
 * Con la lista numerada genérica las 29 respuestas salían con doble número («1. 1. …»),
 * las citas del sustento como puntos 30 y 31 vacíos y los factores, las evidencias y la
 * información adicional no se veían. Cada fila trae su `bloque` y su `texto`; el sustento
 * va en su propia lista bajo su subtítulo, con el mismo texto que el documento.
 */
/** Un valor del contenido del informe como texto: solo lo que ya es texto o número; nunca «[object Object]». */
const comoTexto = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
};
const textoDe = (f: Record<string, unknown>): string => comoTexto(f.texto);
/** Una fila del numeral 9.10 como la lee el resumen: por partes si el servidor las trae
 *  (`valor`, `observacion`, `derivada`), o por su `texto` si es de una versión anterior. */
const comoRespuestaResumida = (f: Record<string, unknown>): RespuestaResumida => ({
  numero: Number(f.numero),
  corto: comoTexto(f.corto),
  texto: textoDe(f),
  ...('valor' in f
    ? { valor: comoTexto(f.valor) || null, observacion: comoTexto(f.observacion) || null, derivada: f.derivada === true }
    : {}),
});
function SeccionDeLaAmpliacion({ seccion }: Readonly<{ seccion: SeccionDelInforme }>) {
  const filas = seccion.filas as Record<string, unknown>[];
  const de = (bloque: string) => filas.filter((f) => f.bloque === bloque);
  const cabecera = de('numeral_910_cabecera')[0];
  const sustento = de('sustento');
  const registro = de('registro')[0];
  // Informes compuestos antes de este arreglo: sin `texto` en el sustento y sin bloques
  // de factores; se lee lo que haya (la cita) y el resto sigue en el texto del documento.
  const lineaDelSustento = (f: Record<string, unknown>) => {
    if (textoDe(f)) return textoDe(f);
    const anexo = comoTexto(f.anexo);
    const cita = `${comoTexto(f.orden)}. ${comoTexto(f.cita)}`;
    return anexo ? `${cita} · ${anexo}` : cita;
  };
  return (
    <div className="text-body space-y-3">
      {cabecera && <p className="font-medium">{textoDe(cabecera)}</p>}
      {/* B10 (QA 01-10-2026): pregunta, respuesta y comentario, por bloques con su borde. */}
      <RespuestasDelInstructivo contexto="informe" nivelDelTitulo={4} aria-label="Respuestas del numeral 9.10"
        respuestas={de('numeral_910').map(comoRespuestaResumida)} />
      {['factores', 'evidencias', 'informacion_adicional'].map((bloque) => de(bloque).map((f) => (
        <p key={bloque}>{textoDe(f)}</p>
      )))}
      {(sustento.length > 0 || registro) && (
        <div>
          <p className="font-medium">Sustento técnico y bibliográfico</p>
          {sustento.length > 0
            ? (
              <ol className="list-none pl-0 space-y-1" aria-label="Sustento técnico y bibliográfico">
                {sustento.map((f) => <li key={comoTexto(f.orden)}>{lineaDelSustento(f)}</li>)}
              </ol>
            )
            : <p className="text-ink-soft">Sin sustento registrado.</p>}
        </div>
      )}
      {registro && <p className="text-ink-soft">{textoDe(registro)}</p>}
    </div>
  );
}

/** La pestaña de la evaluación donde se resuelve cada faltante (`campo` del backend). */
export type PestanaDelFaltante = 'investigacion' | 'causalidad' | 'gravedad';
const PESTANA_DEL_FALTANTE: Record<string, PestanaDelFaltante> = {
  instructivo: 'investigacion', causalidad: 'causalidad', conclusiones: 'causalidad', gravedad: 'gravedad',
};
// El NOMBRE EXACTO de la pestaña (18-sep-2026): «Analisis de causas» hacía creer que faltaba la
// investigación cuando lo que faltaba era la causalidad. Una línea por pestaña, no por campo.
const NOMBRE_DE_LA_PESTANA: Record<PestanaDelFaltante, string> = {
  investigacion: 'Investigación (29 puntos)', causalidad: 'Causalidad y conclusiones', gravedad: 'Gravedad',
};

type FaltantePorPestana = { clave: string; nombre: string; pestana?: PestanaDelFaltante; detalles: string[]; bloqueante: boolean };

/** Agrupa los faltantes por la pestaña donde se resuelven; lo que no tiene pestaña conocida va con su sección. */
const porPestana = (faltantes: FaltanteDelInforme[]): FaltantePorPestana[] => {
  const grupos = new Map<string, FaltantePorPestana>();
  for (const f of faltantes) {
    const pestana = PESTANA_DEL_FALTANTE[f.campo];
    const clave = pestana ?? `seccion:${f.seccion}`;
    const grupo = grupos.get(clave) ?? { clave, nombre: pestana ? NOMBRE_DE_LA_PESTANA[pestana] : f.seccion, pestana, detalles: [], bloqueante: false };
    grupo.detalles.push(f.detalle);
    grupo.bloqueante ||= f.bloqueante;
    grupos.set(clave, grupo);
  }
  return [...grupos.values()];
};

/** Lo que falta para elaborar, antes de intentarlo (viene en la evaluación si el backend lo calcula). */
function Faltantes({ faltantes, onIr }: Readonly<{ faltantes: FaltanteDelInforme[]; onIr?: (pestana: PestanaDelFaltante) => void }>) {
  const grupos = porPestana(faltantes);
  const hayBloqueantes = grupos.some((g) => g.bloqueante);
  return (
    <Alert tone={hayBloqueantes ? 'warning' : 'info'} title={hayBloqueantes ? 'Antes de elaborar el informe falta completar:' : 'Recomendado antes de elaborar:'}>
      <ul className="list-disc pl-4 mt-1 space-y-1" aria-label="Faltantes para el informe">
        {grupos.map((g) => (
          <li key={g.clave} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span><span className="font-medium">{g.nombre}</span> · {g.detalles.join(' ')}{g.bloqueante ? '' : ' (opcional)'}</span>
            {onIr && g.pestana && (
              <Button size="sm" variant="secondary" icon={ArrowRight} onClick={() => onIr(g.pestana as PestanaDelFaltante)} aria-label={`Ir a ${g.nombre}`}>Ir</Button>
            )}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * La pestaña antes de elaborar: qué falta (con el enlace a su pestaña), el error del último
 * intento y el botón. Con algo bloqueante pendiente el botón no se ofrece activo: la lista
 * dice qué falta y lleva a la pestaña. El servidor lo vuelve a comprobar (422) por si la
 * vista está desactualizada.
 */
function AntesDeElaborar({ faltantes, fallo, soloLectura, ocupado, onElaborar, onIrAEvaluacion }: Readonly<{
  faltantes?: FaltanteDelInforme[];
  fallo: Fallo | null;
  soloLectura: boolean;
  ocupado: boolean;
  onElaborar: () => void;
  onIrAEvaluacion?: (pestana?: PestanaDelFaltante) => void;
}>) {
  const bloqueado = (faltantes ?? []).some((f) => f.bloqueante);
  return (
    <Card title="Informe FAR-03" icon={FileText}
      subtitle="Se elabora con la evaluación completa. Queda en borrador hasta que lo envías a aprobación.">
      {faltantes && faltantes.length > 0 && <div className="mb-3"><Faltantes faltantes={faltantes} onIr={onIrAEvaluacion} /></div>}
      {fallo && (
        <div className="mb-3">
          <Alert tone="warning" title={fallo.message === MENSAJE_SIN_CAUSALIDAD ? MENSAJE_SIN_CAUSALIDAD : 'No se pudo elaborar el informe'} detalleTecnico={fallo.detalleTecnico}>
            {fallo.message === MENSAJE_SIN_CAUSALIDAD
              ? <>{onIrAEvaluacion && <Button size="sm" variant="secondary" icon={ArrowRight} onClick={() => onIrAEvaluacion('causalidad')}>Evaluación</Button>}</>
              : fallo.message}
          </Alert>
        </div>
      )}
      {!soloLectura && (
        <Button icon={FileText} onClick={onElaborar} disabled={ocupado || bloqueado}
          title={bloqueado ? 'Completa la evaluación para elaborar el informe' : undefined}>
          Elaborar FAR-03
        </Button>
      )}
      {soloLectura && <p className="text-meta text-ink-soft">El caso todavía no tiene informe elaborado.</p>}
    </Card>
  );
}

/** C9: los títulos de las secciones redactables que siguen vacías (sin ellas no se envía). */
const redactablesVacias = (informe: Informe): string[] =>
  informe.secciones.filter((s) => REDACTABLES.has(s.clave) && !(s.texto ?? '').trim()).map((s) => s.titulo);

/**
 * HU-40-C7: el bloque de aprobación dice quién, cargo, fecha y versión, sin imagen de
 * firma: es el texto de la constancia de esta versión. Sin constancia (informe aprobado
 * antes del Sprint 3) queda lo que ya decía.
 */
const tituloDeLaAprobacion = (informe: Informe, zona: string | null): string => {
  const por = informe.aprobado_por ? ` por ${informe.aprobado_por}` : '';
  const cuando = informe.fecha_aprobacion ? ` · ${fecha(informe.fecha_aprobacion, zona)}` : '';
  return constanciaDeLaAprobacion(informe)?.texto ?? `Informe aprobado${por}${cuando}`;
};

/** El aviso del informe aprobado: la constancia de la versión y su código. */
function AvisoDeAprobacion({ informe, zonaHoraria }: Readonly<{ informe: Informe; zonaHoraria: string | null }>) {
  return (
    <Alert tone="success" title={tituloDeLaAprobacion(informe, zonaHoraria)}>
      {informe.codigo ? <>Código <Mono>{informe.codigo}</Mono>. </> : null}Un cambio posterior exige una versión nueva.
    </Alert>
  );
}

/** HU-14-C10: las versiones enviadas a aprobación, congeladas; cada una se abre tal cual se envió. */
function VersionesEnviadas({ informe, zonaHoraria }: Readonly<{ informe: Informe; zonaHoraria: string | null }>) {
  const [versionAbierta, setVersionAbierta] = useState<number | null>(null);
  const versionAbiertaDatos = informe.versiones.find((v) => v.version === versionAbierta) ?? null;
  return (
    <Card title="Versiones enviadas" icon={History} subtitle="Cada envío a aprobación queda congelado con su texto.">
      <ul className="divide-y divide-hairline" aria-label="Versiones del informe">
        {informe.versiones.map((v) => (
          <li key={v.version} className="py-2 flex flex-wrap items-center gap-3 text-meta">
            <Mono className="font-semibold">v{v.version}</Mono>
            <span className="text-ink-soft">{v.enviado_por ?? '—'} · {fecha(v.enviado_en, zonaHoraria)}</span>
            {v.contenido.plazo_interno && <span className="text-ink-soft">{v.contenido.plazo_interno}</span>}
            <Button size="sm" variant="secondary" onClick={() => setVersionAbierta(versionAbierta === v.version ? null : v.version)}>
              {versionAbierta === v.version ? 'Cerrar' : `Ver v${v.version}`}
            </Button>
          </li>
        ))}
      </ul>
      {versionAbiertaDatos && (
        <div className="mt-3 border-t border-hairline pt-3 space-y-2" aria-label={`Contenido de la versión ${versionAbiertaDatos.version}`}>
          {versionAbiertaDatos.contenido.secciones.map((s) => (
            <p key={s.clave} className="text-meta"><b>{s.numero}. {s.titulo}:</b> <span className="whitespace-pre-wrap">{s.texto ?? '—'}</span></p>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * HU-18: el informe observado. El motivo vigente y las rondas anteriores se leen siempre; el
 * panel para decir qué se subsanó y reenviar, solo si el servidor ofrece `subsanar` (D-S3-03).
 */
function PanelDeSubsanacion({ informe, zonaHoraria, puedeSubsanar, fallo, error, queSeSubsano, pistaDeEnvio, deshabilitado, ocupado, onCambiar, onReenviar }: Readonly<{
  informe: Informe;
  zonaHoraria: string | null;
  puedeSubsanar: boolean;
  /** El fallo del reenvío que no es del campo (409, red…). */
  fallo: Fallo | null;
  /** El aviso bajo «Qué se subsanó» (vacío o el 422 del servidor). */
  error: string | null;
  queSeSubsano: string;
  /** C9: lo que falta redactar para poder reenviar. */
  pistaDeEnvio: string | undefined;
  deshabilitado: boolean;
  ocupado: boolean;
  onCambiar: (texto: string) => void;
  onReenviar: () => void;
}>) {
  const vigente = observacionVigente(informe);
  return (
    <div className="mb-3 space-y-2" data-testid={puedeSubsanar ? undefined : 'subsanacion-sin-permiso'}>
      {vigente && (
        <Alert data-testid="subsanacion-motivo-vigente" tone="warning" title={`Observado (ronda ${vigente.iteracion}): ${vigente.motivo}`}>
          {vigente.observado_por} · {fecha(vigente.observado_en, zonaHoraria)}
        </Alert>
      )}
      <RondasAnteriores informe={informe} />
      {puedeSubsanar && (
        <div className="space-y-2">
          {fallo && (
            <Alert data-testid="subsanacion-error-envio" tone={fallo.estado === 409 ? 'warning' : 'danger'}
              title="No se pudo reenviar" detalleTecnico={fallo.detalleTecnico}>
              {fallo.message}
            </Alert>
          )}
          <Field label="Qué se subsanó" required error={error}
            contador={`${queSeSubsano.length} / ${TOPE_DE_LA_SUBSANACION} caracteres`}>
            <Textarea data-testid="subsanacion-form-que-se-subsano" rows={2} value={queSeSubsano} maxLength={TOPE_DE_LA_SUBSANACION}
              onChange={(e) => onCambiar(e.target.value)} />
          </Field>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {pistaDeEnvio && <span className="text-meta text-ink-soft" role="note">{pistaDeEnvio}</span>}
            {/* HU-18-C1 pide «(v{n+1})» en la etiqueta con la versión que dará el servidor
                (`version_siguiente`); el contrato no la trae y la pantalla no suma 1. */}
            <Button data-testid="subsanacion-reenviar" icon={Send} onClick={onReenviar} disabled={deshabilitado} title={pistaDeEnvio}>
              {ocupado ? 'Reenviando…' : 'Reenviar a aprobación'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function InformeFar03({ casoId, zonaHoraria, actor, soloLectura = false, faltantes, onIrAEvaluacion, onCambio, topeDeSeccion }: Readonly<{
  casoId: string;
  /** RN-82: la zona del país del caso (`useZonaDelPais`), en la que se leen las fechas del informe; `null` = Lima. */
  zonaHoraria: string | null;
  /** Quién firma la elaboración y la redacción: esos dos cuerpos todavía llevan el autor (AT S3 §2.0 no los retira). */
  actor: string;
  /** La máscara de la sesión (invitado, aprobador): resta a lo que ofrece el servidor, nunca agrega. */
  soloLectura?: boolean;
  /**
   * HU-14: el tope de caracteres de una sección redactable, leído de `parametro`
   * (`evaluacion_limites_de_texto.redaccion_seccion_caracteres`). Sin él rige el techo del
   * esquema HTTP (20 000), que existe igual: el parámetro puede bajarlo, no subirlo.
   */
  topeDeSeccion?: number;
  /** Lo que la evaluación dice que falta para el informe, si el backend lo calcula. */
  faltantes?: FaltanteDelInforme[];
  /** C4: el enlace a la evaluación cuando falta algo; `pestana` dice a cuál (sin ella, causalidad). */
  onIrAEvaluacion?: (pestana?: PestanaDelFaltante) => void;
  /** Se avisa al padre cuando el informe cambia de estado (la cabecera del caso lo refleja). */
  onCambio?: (informe: Informe) => void;
}>) {
  const [informe, setInformeLocal] = useState<Informe | null>(null);
  const [sinInforme, setSinInforme] = useState(false);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  // Error del reenvío (HU-18): el de «Qué se subsanó» va bajo el campo; el resto, en el panel.
  const [errorDeSubsanacion, setErrorDeSubsanacion] = useState<string | null>(null);
  const [falloDeSubsanacion, setFalloDeSubsanacion] = useState<Fallo | null>(null);
  // El acuse del acto que congela: enviar (BUG-898) o reenviar (HU-18). Lo arma la pantalla
  // con el código, la versión y la ronda que devolvió el servidor.
  const [exito, setExito] = useState<{ texto: string; testid: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [lectura, setLectura] = useState(0);
  // Cuantas secciones tienen un guardado en vuelo: mientras haya una, no se envia.
  const [guardando, setGuardando] = useState(0);
  const alGuardar = (activo: boolean) => setGuardando((n) => Math.max(0, n + (activo ? 1 : -1)));
  const [queSeSubsano, setQueSeSubsano] = useState('');
  const setInforme = (i: Informe) => { setInformeLocal(i); onCambio?.(i); };
  const nombreDe = useNombresDeLasAcciones();

  useEffect(() => {
    let vivo = true;
    setFallo(null);
    verInforme(casoId)
      .then((i) => { if (vivo) { setInformeLocal(i); setSinInforme(false); } })
      .catch((e) => { if (!vivo) return; const f = aFallo(e); if (f.estado === 404) setSinInforme(true); else setFallo(f); });
    return () => { vivo = false; };
  }, [casoId, lectura]);

  const elaborar = async () => {
    setFallo(null);
    setOcupado(true);
    try {
      setInforme(await elaborarInforme(casoId, actor));
      setSinInforme(false);
    } catch (e) {
      const f = aFallo(e);
      // C4: solo cuando lo ÚNICO que falta es la causalidad se usa la frase de la HU; si
      // faltan más cosas (instructivo, gravedad…) el mensaje del servidor las nombra.
      const soloCausalidad = /causalidad/i.test(f.message) && !/instructivo|gravedad|conclusiones/i.test(f.message);
      setFallo(f.estado === 422 && soloCausalidad ? { ...f, message: MENSAJE_SIN_CAUSALIDAD } : f);
    } finally {
      setOcupado(false);
    }
  };

  const enviar = async () => {
    setFallo(null);
    setExito(null);
    setOcupado(true);
    try {
      const enviado = await enviarInformeAAprobacion(casoId);
      setInforme(enviado);
      setExito({ texto: `Informe ${enviado.codigo ?? ''} v${enviado.version} enviado a aprobación`, testid: 'informe-exito' });
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  const reenviar = async () => {
    setFalloDeSubsanacion(null);
    setExito(null);
    // HU-18-C2: sin decir qué se subsanó no se reenvía; el aviso va bajo el campo.
    if (!queSeSubsano.trim()) { setErrorDeSubsanacion(MENSAJE_SIN_SUBSANACION); return; }
    setErrorDeSubsanacion(null);
    setOcupado(true);
    try {
      const reenviado = await subsanarInforme(casoId, queSeSubsano.trim());
      setInforme(reenviado);
      setQueSeSubsano('');
      setExito({ texto: `Informe v${reenviado.version} reenviado a aprobación (ronda ${reenviado.iteraciones})`, testid: 'subsanacion-exito' });
    } catch (e) {
      const f = aFallo(e);
      if (f.estado === 422) setErrorDeSubsanacion(f.message);
      else setFalloDeSubsanacion(f);
    } finally {
      setOcupado(false);
    }
  };

  if (fallo && !informe && !sinInforme) {
    return (
      <Alert data-testid="subsanacion-error" tone={fallo.estado === 403 ? 'warning' : 'danger'} title="No se pudo abrir el informe" detalleTecnico={fallo.detalleTecnico}>
        {fallo.message}{fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
        <div className="mt-2"><Button size="sm" variant="secondary" icon={RefreshCw} onClick={() => setLectura((n) => n + 1)}>Reintentar</Button></div>
      </Alert>
    );
  }

  if (sinInforme || !informe) {
    if (!sinInforme) return <p data-testid="subsanacion-cargando" className="p-6 text-body text-ink-soft">Abriendo el informe…</p>;
    return <AntesDeElaborar faltantes={faltantes} fallo={fallo} soloLectura={soloLectura} ocupado={ocupado} onElaborar={() => { elaborar(); }} onIrAEvaluacion={onIrAEvaluacion} />;
  }

  // R1: el texto del servidor (`informe.texto_del_estado`); el glosario da el tono y cubre un
  // servidor anterior que no lo trae.
  const estado = { ...estadoDelInforme(informe.estado), ...(informe.texto_del_estado ? { texto: informe.texto_del_estado } : {}) };
  // §8.1 R4: lo que se ofrece sale de `acciones_disponibles`; la máscara de la sesión solo resta.
  const puedeRedactar = ofrece(informe, 'redactar') && !soloLectura;
  const puedeEnviar = ofrece(informe, 'enviar') && !soloLectura;
  const puedeSubsanar = ofrece(informe, 'subsanar') && !soloLectura;
  // C9: sin Antecedentes, Demanda y Conclusión redactados no se envía (ni se reenvía).
  const vacias = redactablesVacias(informe);
  const faltaRedactar = vacias.length > 0;
  const pistaDeEnvio = faltaRedactar ? `Para enviar completa: ${vacias.join(', ')}` : undefined;

  return (
    <div className="space-y-4">
      {fallo && <Alert data-testid="evaluacion-error-envio" tone="danger" title="No se pudo guardar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
      {exito && <Alert data-testid={exito.testid} tone="success" title={exito.texto} onCerrar={() => setExito(null)} />}
      {informe.estado === 'aprobado' && <AvisoDeAprobacion informe={informe} zonaHoraria={zonaHoraria} />}
      {/* HU-49-C5: un grave sin ampliar se señala con el aviso de la cabecera de la evaluación,
          que se ve en todas las pestañas. Aquí se repetía el mismo bloque y ampliar no bloquea
          el envío a aprobación (QA 01-10-2026, B3): se quitó el de esta pestaña. */}
      <Card
        title={<span data-testid="informe-titulo">Informe FAR-03 · {informe.codigo ?? 'sin código'} · v{informe.version}</span>}
        icon={FileText}
        subtitle={informe.plazo_interno ?? undefined}
        actions={
          <span className="inline-flex flex-wrap items-center gap-2">
            <StatusBadge data-testid="informe-estado" tone={estado.tono}>{estado.texto}</StatusBadge>
            {!informe.es_editable && informe.motivo_de_no_editable && (
              <StatusBadge tone="neutral" icon={Lock}>{informe.motivo_de_no_editable}</StatusBadge>
            )}
            {puedeEnviar && (
              <>
                {pistaDeEnvio && <span className="text-meta text-ink-soft" role="note">{pistaDeEnvio}</span>}
                <Button data-testid="informe-enviar" size="sm" icon={Send} onClick={() => { enviar(); }} disabled={ocupado || guardando > 0 || faltaRedactar} title={pistaDeEnvio}>
                  Enviar a aprobación
                </Button>
              </>
            )}
          </span>
        }
      >
        {/* HU-18-C5/C6: el escalamiento lo dice el servidor; el tono, su booleano. */}
        {informe.texto_del_escalamiento && (
          <div className="mb-3">
            <Alert data-testid="subsanacion-escalamiento" tone={informe.escalado_a_gerencia ? 'warning' : 'info'} icon={AlertTriangle}
              title={informe.texto_del_escalamiento} />
          </div>
        )}
        {informe.estado === 'observado' && (
          <PanelDeSubsanacion informe={informe} zonaHoraria={zonaHoraria} puedeSubsanar={puedeSubsanar} fallo={falloDeSubsanacion} error={errorDeSubsanacion}
            queSeSubsano={queSeSubsano} pistaDeEnvio={pistaDeEnvio} deshabilitado={ocupado || guardando > 0 || faltaRedactar} ocupado={ocupado}
            onCambiar={(texto) => { setQueSeSubsano(texto); setErrorDeSubsanacion(null); }} onReenviar={() => { reenviar(); }} />
        )}
        {informe.estado !== 'observado' && <div className="mb-3"><RondasAnteriores informe={informe} /></div>}
        <div className="space-y-3" data-testid={puedeRedactar ? 'informe-editor' : undefined} aria-label="Secciones del FAR-03">
          {informe.secciones.map((s) => (
            <Seccion key={s.clave} seccion={puedeRedactar ? s : { ...s, editable: false }} informe={informe} actor={actor} onCambio={setInforme} onFallo={setFallo} onGuardando={alGuardar} nombreDe={nombreDe}
              tope={Math.min(topeDeSeccion ?? TOPE_DE_SECCION_DEL_ESQUEMA, TOPE_DE_SECCION_DEL_ESQUEMA)} />
          ))}
        </div>
      </Card>

      {informe.versiones.length > 0 && <VersionesEnviadas informe={informe} zonaHoraria={zonaHoraria} />}
    </div>
  );
}
